import * as yup from 'yup';

export const datasetNameRules = {
  min: 3,
  max: 120,
  pattern: /^[ A-Za-z0-9_@./#&+-]*$/,
} as const;

export const datasetMdtIdRules = {
  max: 40,
  pattern: /^[A-Za-z0-9]+$/,
} as const;

export const MAX_SECONDARY_DATA_STEWARDS = 2;
export const MAX_PROVENANCE_LENGTH = 500;
export const MAX_DESCRIPTION_LENGTH = 500;

export const datasetDetailsSchema: yup.AnyObjectSchema = yup
  .object({
    name: yup
      .string()
      .transform((v) => (typeof v === 'string' ? v.trim() : v))
      .min(
        datasetNameRules.min,
        `Dataset name should contain at least ${datasetNameRules.min} characters and up to ${datasetNameRules.max} characters`,
      )
      .max(
        datasetNameRules.max,
        `Dataset name should contain at least ${datasetNameRules.min} characters and up to ${datasetNameRules.max} characters`,
      )
      .matches(datasetNameRules.pattern, 'Only alphanumeric values, spaces and _@./#&+- are allowed')
      .required('Dataset name is a required field'),
    mdt_id: yup
      .string()
      .nullable()
      .transform((value) => (typeof value === 'string' ? value.trim() : value) || null)
      .when('$isEdit', {
        is: false,
        then: (schema) =>
          schema
            .max(datasetMdtIdRules.max, `Dataset ID should contain up to ${datasetMdtIdRules.max} characters`)
            .matches(datasetMdtIdRules.pattern, 'Only alphanumeric values are allowed.'),
        otherwise: (schema) => schema,
      })
      .notRequired(),
    cost_center_id: yup.mixed().nullable().notRequired(),
    wbs_code_id: yup
      .mixed()
      .nullable()
      .when(['$hasLockedStatus', 'cost_center_id'], {
        is: (hasLockedStatus: boolean, costCenterId: unknown) => !hasLockedStatus && !costCenterId,
        then: (schema) => schema.required('WBS code or cost center is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    geo_location_tags: yup
      .array()
      .of(yup.string().required())
      .min(1, 'Data location is a required field')
      .required('Data location is a required field'),
    status: yup.string().when('$isEdit', {
      is: true,
      then: (schema) => schema.required('Dataset status is a required field'),
      otherwise: (schema) => schema.notRequired(),
    }),
    source_ou: yup.string().required('OU owner is a required field'),
    de_identified: yup.string().required('De-identified status is a required field'),
    data_classification: yup.string().required('Data classification is a required field'),
    description: yup
      .string()
      .transform((v) => (typeof v === 'string' ? v.trim() : v))
      .max(MAX_DESCRIPTION_LENGTH, `Description should contain up to ${MAX_DESCRIPTION_LENGTH} characters`)
      .required('Description is a required field'),
    provenance: yup
      .string()
      .transform((v) => (typeof v === 'string' ? v.trim() : v))
      .max(MAX_PROVENANCE_LENGTH, `Provenance should contain up to ${MAX_PROVENANCE_LENGTH} characters`)
      .required('Provenance is a required field'),
  })
  .required();

export const dataTagsSchema: yup.AnyObjectSchema = yup
  .object({
    collection_source: yup.string().required('Data source category is a required field'),
    data_format: yup
      .array()
      .of(yup.string().required())
      .min(1, 'Data source type is a required field')
      .required('Data source type is a required field'),
    legal_relationship_tag: yup.string().required('Legal relationship tag is a required field'),
    legal_relationship_qualifier_tag: yup.string().notRequired(),
    restriction_tag: yup.array().of(yup.string()).notRequired(),
    anatomy: yup.array().of(yup.string().notRequired()),
    modality: yup.array().of(yup.string().notRequired()),
    dimensionality: yup.array().of(yup.string().notRequired()),
  })
  .required();

export const datasetMembersSchema: yup.AnyObjectSchema = yup
  .object({
    data_owner_name: yup.string().required('Primary data steward is a required field'),
    data_owner_network_id: yup.string().required('Primary data steward network ID is a required field'),
    secondary_data_owners: yup
      .array()
      .of(yup.string().required())
      .transform((value) => (Array.isArray(value) ? value.filter((v) => v) : value))
      .max(MAX_SECONDARY_DATA_STEWARDS)
      .notRequired(),
    secondary_data_owner_network_ids: yup
      .array()
      .of(yup.string())
      .transform((value) => (Array.isArray(value) ? value.filter((v) => v) : value))
      .notRequired(),
    data_uploaders: yup
      .array()
      .of(yup.string().required())
      .transform((value) => (Array.isArray(value) ? value.filter((v) => v) : value))
      .notRequired(),
  })
  .required();

export const datasetSchema: yup.AnyObjectSchema = datasetDetailsSchema
  .concat(dataTagsSchema)
  .concat(datasetMembersSchema);
